import { useAsyncData, useState } from '#app'
import { useFilters, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError, MarketCountryCode } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { sleep } from '@backmarket/utils/async/sleep'

import type {
  GetListingsV2Query,
  GetListingsV2Response,
  Listing,
  ListingUpdateBody,
  ListingV2,
  PublicationState,
} from '~/api/listings/types'
import {
  LISTING_PUBLICATION_STATE_COMPUTING_INTERVAL,
  PUBLICATION_STATE,
} from '~/scopes/listings/Listings.constants'

import { INITIAL_FILTERS_VALUES } from '../components/Filters/Filters.constants'
import { listingCountParamsTransformer } from '../helpers/listingCountParamsTransformer'

import translations from './useListings.translations'

export function useListings() {
  const route = useRoute()
  const i18n = useI18n()
  const { openSuccessToast, openErrorToast } = useTheToast()

  const logger = useLogger()

  const listings = useState<GetListingsV2Response['results']>(
    'listings',
    () => [],
  )
  const listingsCount = useState('listingsCount', () => 0)

  const nextCursor = useState<GetListingsV2Response['nextCursor']>(
    'listingNextCursor',
    () => null,
  )
  const prevCursor = useState<GetListingsV2Response['prevCursor']>(
    'listingPrevCursor',
    () => null,
  )
  const isListingRefreshing = useState('isListingRefreshing', () => false)

  const $httpFetch = useHttpFetch()

  const direction = ref<GetListingsV2Query['direction']>(undefined)
  const publicationState = ref<PublicationState>(
    PUBLICATION_STATE.ONLINE as PublicationState,
  )

  const formattedPublicationState = computed(() => {
    return publicationState.value === PUBLICATION_STATE.ONLINE
      ? [
          PUBLICATION_STATE.ONLINE,
          PUBLICATION_STATE.OFFLINE,
          PUBLICATION_STATE.MISSING_PRICE_OR_COMMENT,
        ]
      : publicationState.value
  })

  const { filtersValues, sanitizedFiltersValues, submitFilters, resetFilters } =
    useFilters(
      {
        ...INITIAL_FILTERS_VALUES,
      },
      { isUsingCursorPagination: true },
    )

  function getListings() {
    if (
      Object.values(PUBLICATION_STATE).includes(
        route.meta?.publicationState as unknown as PublicationState,
      )
    ) {
      publicationState.value = route.meta
        ?.publicationState as unknown as PublicationState
    }

    const { refresh, status } = useAsyncData(
      async () => {
        try {
          const listingsResponse = $httpFetch(
            '/api/seller-experience/listings-new',
            {
              query: {
                ...sanitizedFiltersValues.value,
                publicationStates:
                  formattedPublicationState.value as PublicationState[],
                ...(direction.value &&
                  (nextCursor.value || prevCursor.value) && {
                    direction: direction.value,
                    cursorId:
                      direction.value === 'next' && nextCursor.value
                        ? nextCursor.value
                        : (prevCursor.value ?? undefined),
                  }),
              } as unknown as GetListingsV2Query,
            },
          )

          // TODO: Skip count request if the "normal" query params do not change i.e a market filter
          // For example if the only the cursorId or direction is changing.

          const countResponse = $httpFetch(
            '/api/seller-experience/listings/count',
            {
              query: {
                ...listingCountParamsTransformer({
                  ...sanitizedFiltersValues.value,
                  publicationStates:
                    formattedPublicationState.value as PublicationState[],
                } as unknown as GetListingsV2Query),
              },
            },
          )

          return Promise.all([listingsResponse, countResponse])
        } catch (error) {
          const { detail: content, title } = HttpApiError.fromAnyError(
            error as Error,
          )
          openErrorToast({
            title: title || i18n(translations.errorTitle),
            content: content || i18n(translations.fetchListingsErrorContent),
          })
          throw error
        }
      },
      {
        watch: [sanitizedFiltersValues, formattedPublicationState],
        transform: (data) => {
          const [listingsResponse, countResponse] = data

          listings.value = listingsResponse?.results ?? []
          nextCursor.value = listingsResponse?.nextCursor ?? null
          prevCursor.value = listingsResponse?.prevCursor ?? null
          listingsCount.value = countResponse?.count ?? 0

          return [listingsResponse, countResponse]
        },
      },
    )

    return {
      refreshListings: refresh,
      status,
    }
  }

  async function getListing(listingId: string) {
    try {
      const data = await $httpFetch(
        '/api/seller-experience/listings/:listingId',
        {
          pathParams: {
            listingId,
          },
        },
      )

      return data
    } catch (error) {
      const { detail: content, title } = HttpApiError.fromAnyError(
        error as Error,
      )
      openErrorToast({
        title: title || i18n(translations.errorTitle),
        content: content || i18n(translations.fetchListingErrorContent),
      })
      throw error
    }
  }

  async function publishListing(listingId: ListingV2['id']) {
    try {
      await $httpFetch('/api/seller-experience/listings/:listingId/publish', {
        method: 'POST',
        pathParams: {
          listingId,
        },
      })

      openSuccessToast({
        title: i18n(translations.publishSuccessTitle),
        content: i18n(translations.publishSuccessContent),
      })
    } catch (error) {
      const { detail: content, title } = HttpApiError.fromAnyError(
        error as Error,
      )
      openErrorToast({
        title: title || i18n(translations.errorTitle),
        content: content || i18n(translations.publishErrorContent),
      })
      throw error
    }
  }

  async function archiveListing(listingId: ListingV2['id']) {
    try {
      await $httpFetch('/api/seller-experience/listings/:listingId/archive', {
        method: 'POST',
        pathParams: {
          listingId,
        },
      })

      openSuccessToast({
        title: i18n(translations.archiveSuccessTitle),
        content: i18n(translations.archiveSuccessContent),
      })
    } catch (error) {
      const { detail: content, title } = HttpApiError.fromAnyError(
        error as Error,
      )
      openErrorToast({
        title: title || i18n(translations.errorTitle),
        content: content || i18n(translations.archiveErrorContent),
      })
      throw error
    }
  }

  async function refreshListing(updatedListing: Listing | null) {
    if (listings.value.length && updatedListing) {
      const listingIndex = listings.value.findIndex(
        (listing) => listing.id === updatedListing.id,
      )

      if (listingIndex === -1) return

      const originalListing = listings.value[listingIndex]

      // Merge markets selectively - only update markets that exist in the original listing
      // This is because the getListing endpoint returns a listing with ALL markets some of which
      // may not be in the original listing (for example if the listings were filtered for specficc market(s))

      // List all markets in the updated listing
      const allUpdatedMarkets = [
        ...Object.keys(updatedListing.markets.offline),
        ...Object.keys(updatedListing.markets.withBackbox),
        ...Object.keys(updatedListing.markets.withoutBackbox),
      ] as MarketCountryCode[]

      const mergedMarkets = {
        offline: { ...originalListing.markets.offline },
        withBackbox: { ...originalListing.markets.withBackbox },
        withoutBackbox: { ...originalListing.markets.withoutBackbox },
      }

      allUpdatedMarkets.forEach((marketCode) => {
        // Only process markets that exist in the original listing
        if (
          originalListing.markets.offline[marketCode] ||
          originalListing.markets.withBackbox[marketCode] ||
          originalListing.markets.withoutBackbox[marketCode]
        ) {
          // Remove from all categories first
          delete mergedMarkets.offline[marketCode]
          delete mergedMarkets.withBackbox[marketCode]
          delete mergedMarkets.withoutBackbox[marketCode]

          // Add to the correct category based on what's in the get listing response
          if (updatedListing.markets.offline[marketCode]) {
            mergedMarkets.offline[marketCode] =
              updatedListing.markets.offline[marketCode]
          } else if (updatedListing.markets.withBackbox[marketCode]) {
            mergedMarkets.withBackbox[marketCode] =
              updatedListing.markets.withBackbox[marketCode]
          } else if (updatedListing.markets.withoutBackbox[marketCode]) {
            mergedMarkets.withoutBackbox[marketCode] =
              updatedListing.markets.withoutBackbox[marketCode]
          }
        }
      })

      // Replace the listing in the array with the updated one
      listings.value[listingIndex] = {
        ...originalListing,
        ...updatedListing,
        markets: mergedMarkets,
      }
    }

    isListingRefreshing.value = false
  }

  const listingQueues = new Map<string, Promise<unknown>>()
  const pendingOperationsByListing = ref<Record<string, number>>({})

  // It's important that we do not allow simultaneous updates to the same listing.
  // Each listing update needs to be processed sequentially, including the refresh of the listing data.
  // This is why we use a queue to manage the listing updates.
  // The queue is essentially a list of promises that are resolved in order.
  // New operations are added to the end of the queue.

  async function queueListingOperation<T>(
    listingId: string,
    operation: () => Promise<T>,
  ): Promise<T> {
    if (!listingQueues.has(listingId)) {
      listingQueues.set(listingId, Promise.resolve())
    }

    if (!pendingOperationsByListing.value[listingId]) {
      pendingOperationsByListing.value[listingId] = 0
    }
    pendingOperationsByListing.value[listingId] += 1

    const currentQueue = listingQueues.get(listingId)

    if (!currentQueue) {
      throw new Error(`No queue found for listing ${listingId}`)
    }

    const newQueue = currentQueue
      .then(async () => {
        try {
          const result = await operation()

          return result
        } catch (error) {
          logger.error('[Listings V2] Error in listing queue', {
            error: error as Error,
            listingId,
            owners: ['bot-squad-seller-experience-front'],
          })
          throw error
        }
      })
      .finally(() => {
        pendingOperationsByListing.value[listingId] -= 1

        if (pendingOperationsByListing.value[listingId] === 0) {
          delete pendingOperationsByListing.value[listingId]
          listingQueues.delete(listingId)
        }
      })

    listingQueues.set(listingId, newQueue)

    return newQueue
  }

  async function setWatcherOnListingUpdateStatus(listing: Listing | null) {
    if (!listing) {
      return
    }

    isListingRefreshing.value = true

    const hasPublicationStateComputing = listing.isPublicationStateComputing
    const hasAtLeastOneMarketWithPendingBackbox = Object.values({
      ...listing.markets.offline,
      ...listing.markets.withoutBackbox,
    }).some((market) => market.isBackboxPending)

    if (hasAtLeastOneMarketWithPendingBackbox || hasPublicationStateComputing) {
      await sleep(LISTING_PUBLICATION_STATE_COMPUTING_INTERVAL)

      const freshListing = await getListing(listing.id)

      await setWatcherOnListingUpdateStatus(freshListing)
    } else {
      await refreshListing(listing)
    }
  }

  async function updateListing(listingId: string, body: ListingUpdateBody) {
    return queueListingOperation(listingId, async () => {
      try {
        const data = await $httpFetch(
          '/api/seller-experience/listings/:listingId',
          {
            method: 'PATCH',
            pathParams: {
              listingId,
            },
            body,
          },
        )

        await setWatcherOnListingUpdateStatus(data)
        openSuccessToast({
          title: i18n(translations.updateSuccessTitle),
          content: i18n(translations.updateSuccessContent),
        })
      } catch (error) {
        const { detail: content, title } = HttpApiError.fromAnyError(
          error as Error,
        )
        openErrorToast({
          title: title || i18n(translations.errorTitle),
          content: content || i18n(translations.updateErrorContent),
        })
        throw error
      }
    })
  }

  async function winBackBoxForMarket(
    listingId: string,
    market: MarketCountryCode,
  ) {
    return queueListingOperation(listingId, async () => {
      try {
        const data = await $httpFetch(
          '/api/seller-experience/listings/:listingId/get-backbox',
          {
            method: 'POST',
            pathParams: {
              listingId,
            },
            body: {
              market,
            },
          },
        )

        await setWatcherOnListingUpdateStatus(data)
      } catch (error) {
        const { detail: content, title } = HttpApiError.fromAnyError(
          error as Error,
        )
        openErrorToast({
          title: title || i18n(translations.errorTitle),
          content: content || i18n(translations.winBackboxErrorContent),
        })
        throw error
      }
    })
  }

  async function winAllBackboxes(listingId: string) {
    try {
      const data = await $httpFetch(
        '/api/seller-experience/listings/:listingId/get-all-backboxes',
        {
          method: 'POST',
          pathParams: {
            listingId,
          },
        },
      )

      await setWatcherOnListingUpdateStatus(data)
    } catch (error) {
      const { detail: content, title } = HttpApiError.fromAnyError(
        error as Error,
      )
      openErrorToast({
        title: title || i18n(translations.errorTitle),
        content: content || i18n(translations.winAllBackboxesErrorContent),
      })
      throw error
    }
  }

  async function setSalesMaximizerForAllMarkets(listingId: string) {
    try {
      const data = await $httpFetch(
        '/api/seller-experience/listings/:listingId/set-all-sales-maximizer-prices',
        {
          method: 'POST',
          pathParams: {
            listingId,
          },
        },
      )

      await setWatcherOnListingUpdateStatus(data)
    } catch (error) {
      const { detail: content, title } = HttpApiError.fromAnyError(
        error as Error,
      )
      openErrorToast({
        title: title || i18n(translations.errorTitle),
        content: content || i18n(translations.setSalesMaximizerAllErrorContent),
      })
      throw error
    }
  }

  async function setSalesMaximizer(
    listingId: string,
    market: MarketCountryCode,
  ) {
    return queueListingOperation(listingId, async () => {
      try {
        const data = await $httpFetch(
          '/api/seller-experience/listings/:listingId/set-sales-maximizer-price',
          {
            method: 'POST',
            pathParams: {
              listingId,
            },
            body: {
              market,
            },
          },
        )

        await setWatcherOnListingUpdateStatus(data)
      } catch (error) {
        const { detail: content, title } = HttpApiError.fromAnyError(
          error as Error,
        )
        openErrorToast({
          title: title || i18n(translations.errorTitle),
          content: content || i18n(translations.setSalesMaximizerErrorContent),
        })
        throw error
      }
    })
  }

  async function unlockDeal(listingId: string, market: MarketCountryCode) {
    return queueListingOperation(listingId, async () => {
      const data = await $httpFetch(
        '/api/seller-experience/listings/:listingId/set-deal-price',
        {
          method: 'POST',
          pathParams: {
            listingId,
          },
          body: {
            market,
          },
        },
      )

      await setWatcherOnListingUpdateStatus(data)
    })
  }

  function handleSubmitFilters() {
    // Clear pagination state when submitting filters as the query has changed
    direction.value = undefined
    submitFilters()
  }
  function handleResetFilters() {
    // Clear pagination state when resetting filters as the query has changed
    direction.value = undefined
    resetFilters()
  }

  return {
    listingsCount,
    listings,
    filtersValues,
    direction,
    resetFilters: handleResetFilters,
    submitFilters: handleSubmitFilters,
    nextCursor,
    prevCursor,
    getListings,
    publishListing,
    archiveListing,
    publicationState,
    updateListing,
    isListingRefreshing,
    winBackBoxForMarket,
    winAllBackboxes,
    setSalesMaximizer,
    setSalesMaximizerForAllMarkets,
    unlockDeal,
    sanitizedFiltersValues,
  }
}
